import { useWatch } from 'react-hook-form';
import { useCallback, useMemo } from 'react';
import { useListEditing } from './useListEditing';
/**
 * `useFormListView` is a custom hook designed for managing the view of a list within a form, particularly with 'react-hook-form'.
 * It provides functionalities to watch and manipulate list items, including editing and deleting operations.
 * This hook is useful in scenarios where you need a dynamic list view integrated with form controls.
 *
 * @param {UseFormListViewProps} props - The properties passed to the useFormListView hook. These include:
 * @param {any} path - The path in the form data where the list is stored.
 * @param {any} control - The control object from 'react-hook-form' used for managing the state of form fields.
 * @param {number | null} activeIndex - The index of the currently active (selected or edited) item in the list, or null if none.
 * @param {(value: number | null) => void} onIndexChange - Function to change the active index, typically used to set the item in edit mode.
 *
 * @returns {FormListItem[]} - An array of items where each item represents a list element with additional properties for managing edit and delete operations:
 * - key: A unique key for each list item.
 * - item: The actual data of the list item.
 * - index: The index of the item in the list.
 * - onEdit: Function to set the item in edit mode.
 * - onDelete: Function to delete the item from the list.-
 */
export const useFormListView = ({ path, control, activeIndex, onIndexChange }) => {
    const { fields, remove } = useListEditing({
        control,
        listPath: path
    });
    const list = useWatch({
        control,
        name: path
    });
    const onRemove = useCallback((index) => {
        remove(index);
        onIndexChange(null);
    }, []);
    const items = useMemo(() => (list === null || list === void 0 ? void 0 : list.map((item, index) => {
        var _a;
        return ({
            key: `${((_a = fields[index]) === null || _a === void 0 ? void 0 : _a.id) || 'new'}-${index}`,
            item: item,
            index: index,
            onEdit: onIndexChange,
            onDelete: onRemove
        });
    })) || [], [list, activeIndex]);
    return items;
};
